import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import reviewsAPI from '../services/reviewsAPI';
import config from '../config/reviewsConfig';

const ReviewsSection = () => {
  const { language } = useLanguage();
  const [reviewsData, setReviewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [expandedReview, setExpandedReview] = useState(null);
  
  // Responsive reviews per page
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const reviewsPerPage = isMobile ? 1 : 3;
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile !== isMobile) {
        setCurrentPage(0); // Reset to first page on screen size change
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Fetch reviews from audiosum.app API
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        
        console.log('🔄 Fetching reviews from audiosum.app API...');
        console.log('📡 API URL:', process.env.REACT_APP_REVIEWS_API_URL || 'https://audiosum.app/api');
        
        const data = await reviewsAPI.fetchReviews({ 
          limit: config.reviews.defaultLimit,
          useCache: config.api.cache.enabled 
        });
        
        console.log('✅ Successfully fetched reviews:', data);
        console.log('📊 Number of reviews received:', data?.reviews?.length || 0);
        setReviewsData(data);
      } catch (err) {
        console.error('❌ Error fetching reviews from audiosum.app:', err);
        console.log('🔄 Using fallback reviews instead');
        
        // Use fallback data if API fails
        const fallbackData = reviewsAPI.getFallbackReviews(language);
        console.log('📝 Fallback reviews loaded:', fallbackData?.reviews?.length || 0);
        setReviewsData(fallbackData);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
    
    // Set up polling to keep reviews updated
    let interval;
    if (config.api.polling.enabled) {
      interval = setInterval(fetchReviews, config.api.polling.interval);
    }
    
    return () => clearInterval(interval);
  }, [language]); // Re-fetch when language changes

  // Pagination functions
  const totalPages = reviewsData?.reviews ? Math.ceil(reviewsData.reviews.length / reviewsPerPage) : 0;
  const canGoPrevious = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;
  
  const goToPreviousPage = () => {
    if (canGoPrevious) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  const goToNextPage = () => {
    if (canGoNext) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Get current reviews to display
  const getCurrentReviews = () => {
    if (!reviewsData?.reviews) return [];
    const startIndex = currentPage * reviewsPerPage;
    const endIndex = startIndex + reviewsPerPage;
    return reviewsData.reviews.slice(startIndex, endIndex);
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <i
          key={i}
          className={i < parseInt(rating) ? "fas fa-star" : "far fa-star"}
        />
      );
    }
    return stars;
  };

  // Function to truncate text and add read more functionality
  const truncateText = (text, maxLength = 120) => {
    if (!text || text === "None") return null;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Handle modal for full review
  const openFullReview = (review) => {
    setExpandedReview(review);
  };

  const closeFullReview = () => {
    setExpandedReview(null);
  };

  if (loading) {
    return (
      <div className="reviews-section mb-5">
        <div className="text-center p-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">
            {language === 'ar' ? 'جاري تحميل التقييمات...' : 'Loading reviews...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="reviews-section mb-5">
      {/* Simple Header */}
      <div className="text-center mb-5">
        <h2 
          className="mb-4" 
          style={{ 
            fontWeight: '700',
            fontSize: 'clamp(2rem, 5vw, 2.5rem)',
            color: '#0F172A',
            fontFamily: '"Playfair Display", serif'
          }}
          data-aos="fade-up"
        >
          {language === 'ar' ? 'التقييمات' : 'Reviews'}
        </h2>
        
        {/* Gradient Separator Line */}
        <div 
          className="mx-auto mb-4"
          style={{
            width: '80px',
            height: '4px',
            background: 'linear-gradient(135deg, #6C3AED 0%, #7C3AED 100%)',
            borderRadius: '2px'
          }}
          data-aos="fade-up" data-aos-delay="100"
        />
        
        {/* Star Rating Display - No numbers */}
        {reviewsData?.aggregate_rating && (
          <>
            <div className="d-flex justify-content-center align-items-center mb-2" data-aos="fade-up" data-aos-delay="200">
              {[...Array(5)].map((_, i) => {
                const rating = parseFloat(reviewsData.aggregate_rating.rating_value);
                const isFullStar = i < Math.floor(rating);
                const isHalfStar = i === Math.floor(rating) && rating % 1 >= 0.5;
                
                return (
                  <i 
                    key={i}
                    className={`fas ${isFullStar ? 'fa-star' : isHalfStar ? 'fa-star-half-alt' : 'fa-star'} ${isFullStar || isHalfStar ? 'text-warning' : 'text-muted'}`}
                    style={{ 
                      fontSize: '1.8rem',
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      margin: '0 3px',
                      filter: (isFullStar || isHalfStar) ? 'drop-shadow(0 0 10px rgba(255, 193, 7, 0.3))' : 'none'
                    }}
                  />
                );
              })}
            </div>
            <div className="text-center mb-4" style={{ color: '#64748B', fontSize: '1.1rem', fontWeight: '500' }} data-aos="fade-up" data-aos-delay="300">
              {parseFloat(reviewsData.aggregate_rating.rating_value).toFixed(1)} out of 5 ({reviewsData.aggregate_rating.review_count} {language === 'ar' ? 'تقييمات' : 'reviews'})
            </div>
          </>
        )}
      </div>
      
      {reviewsData?.reviews && reviewsData.reviews.length > 0 ? (
        <React.Fragment>
          {/* Circular Review Cards Design */}
          <div className="reviews-section-wrapper py-4">
            <div className="container">
              <div className="d-flex justify-content-center align-items-start flex-wrap" style={{ gap: '40px' }}>
                {getCurrentReviews().map((review, index) => (
                  <div 
                    key={currentPage * reviewsPerPage + index}
                    className="circular-review-card"
                    style={{
                      width: isMobile ? '300px' : '340px',
                      height: isMobile ? '300px' : '340px',
                      margin: '0 auto'
                    }}
                    data-aos="zoom-in" data-aos-delay={400 + (index * 100)}
                  >
                    <div 
                      className="circular-card position-relative d-flex flex-column justify-content-center align-items-center text-center p-4"
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                        boxShadow: '0 25px 70px rgba(108, 58, 237, 0.4), 0 15px 40px rgba(124, 58, 237, 0.3), 0 8px 25px rgba(108, 58, 237, 0.2)',
                        border: '3px solid rgba(108, 58, 237, 0.15)',
                        transition: 'all 0.4s ease',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        filter: 'drop-shadow(0 10px 30px rgba(108, 58, 237, 0.25))'
                      }}
                      onClick={() => {
                        if (review.reviewBody && review.reviewBody !== "None" && review.reviewBody.length > 100) {
                          openFullReview(review);
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-15px) scale(1.08)';
                        e.currentTarget.style.boxShadow = '0 35px 90px rgba(108, 58, 237, 0.5), 0 20px 50px rgba(124, 58, 237, 0.4), 0 10px 35px rgba(108, 58, 237, 0.35)';
                        e.currentTarget.style.borderColor = 'rgba(108, 58, 237, 0.25)';
                        e.currentTarget.style.filter = 'drop-shadow(0 15px 40px rgba(108, 58, 237, 0.4))';
                        // Add cursor pointer only if review has long text
                        if (review.reviewBody && review.reviewBody !== "None" && review.reviewBody.length > 100) {
                          e.currentTarget.style.cursor = 'pointer';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 25px 70px rgba(108, 58, 237, 0.4), 0 15px 40px rgba(124, 58, 237, 0.3), 0 8px 25px rgba(108, 58, 237, 0.2)';
                        e.currentTarget.style.borderColor = 'rgba(108, 58, 237, 0.15)';
                        e.currentTarget.style.filter = 'drop-shadow(0 10px 30px rgba(108, 58, 237, 0.25))';
                        e.currentTarget.style.cursor = 'default';
                      }}
                    >
                      {/* Decorative background pattern */}
                      <div 
                        className="position-absolute"
                        style={{
                          top: '10%',
                          right: '15%',
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          background: 'radial-gradient(circle, rgba(108, 58, 237, 0.1) 0%, transparent 70%)',
                          zIndex: 1
                        }}
                      />
                      <div 
                        className="position-absolute"
                        style={{
                          bottom: '15%',
                          left: '10%',
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'radial-gradient(circle, rgba(124, 58, 237, 0.1) 0%, transparent 70%)',
                          zIndex: 1
                        }}
                      />
                      
                      <div className="position-relative d-flex flex-column h-100 justify-content-center" style={{ 
                        zIndex: 2, 
                        padding: isMobile ? '15px 10px' : '20px 15px', 
                        minHeight: isMobile ? '200px' : '240px' 
                      }}>
                        {/* User Avatar */}
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center mb-2 mx-auto flex-shrink-0"
                          style={{
                            width: isMobile ? '50px' : '60px',
                            height: isMobile ? '50px' : '60px',
                            minWidth: isMobile ? '50px' : '60px',
                            minHeight: isMobile ? '50px' : '60px',
                            background: 'linear-gradient(135deg, #6C3AED 0%, #7C3AED 100%)',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: isMobile ? '1.2em' : '1.5em',
                            boxShadow: '0 8px 25px rgba(108, 58, 237, 0.3)'
                          }}
                        >
                          {review.author.name[0].toUpperCase()}
                        </div>
                        
                        {/* User Name */}
                        <h6 className="mb-2 flex-shrink-0" style={{ 
                          color: '#2d3748', 
                          fontWeight: '600',
                          fontSize: isMobile ? '1em' : '1.1em',
                          minHeight: '1.2em',
                          lineHeight: '1.2'
                        }}>
                          {review.author.name}
                        </h6>
                        
                        {/* Star Rating */}
                        <div className="mb-2 d-flex justify-content-center flex-shrink-0" style={{ minHeight: '1.2em' }}>
                          {renderStars(review.reviewRating.ratingValue).map((star, i) => (
                            <span key={i} style={{ 
                              color: '#ffc107', 
                              fontSize: isMobile ? '1em' : '1.2em',
                              textShadow: '0 2px 4px rgba(255, 193, 7, 0.3)',
                              margin: '0 1px'
                            }}>
                              {star}
                            </span>
                          ))}
                        </div>
                        
                        {/* Review Text */}
                        <div className="mb-2 flex-grow-1 d-flex flex-column justify-content-center">
                          <p 
                            className="fst-italic mb-1"
                            style={{
                              color: '#4a5568',
                              fontSize: isMobile ? '0.8em' : '0.9em',
                              lineHeight: '1.3',
                              marginBottom: '4px',
                              textAlign: 'center'
                            }}
                          >
                            {review.reviewBody && review.reviewBody !== "None" ? (
                              `"${truncateText(review.reviewBody, isMobile ? 80 : 100)}"`
                            ) : (
                              language === 'ar' 
                                ? 'لم يقدم هذا المستخدم تعليقاً'
                                : 'No comment was made by this user'
                            )}
                          </p>
                          
                          {/* Read More Button - Show only if text is truncated */}
                          {review.reviewBody && review.reviewBody !== "None" && review.reviewBody.length > (isMobile ? 80 : 100) && (
                            <div className="d-flex align-items-center justify-content-center mt-1">
                              <button
                                className="btn btn-link p-0 d-flex align-items-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFullReview(review);
                                }}
                                style={{
                                  fontSize: isMobile ? '0.65em' : '0.75em',
                                  color: '#6C3AED',
                                  textDecoration: 'none',
                                  fontWeight: '500',
                                  border: 'none',
                                  background: 'none'
                                }}
                              >
                                {language === 'ar' ? 'اقرأ المزيد' : 'Read more'}
                                <i className="fas fa-expand-alt ms-1" style={{ fontSize: '0.8em' }} />
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Verified Badge */}
                        <div className="flex-shrink-0">
                          <small 
                            className="text-muted d-flex align-items-center justify-content-center"
                            style={{ 
                              fontSize: isMobile ? '0.65em' : '0.75em',
                              marginTop: '4px'
                            }}
                          >
                            <i className="fas fa-check-circle text-success me-1" />
                            {language === 'ar' ? 'مستخدم معتمد' : 'Verified User'}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

            {/* Bottom Navigation with Buttons and Pagination */}
            {totalPages > 1 && (
              <div className="container mt-4">
                <div className="d-flex justify-content-center align-items-center gap-3">
                  {/* Left Arrow Button */}
                  <button
                    className="btn d-flex align-items-center justify-content-center"
                    onClick={goToPreviousPage}
                    disabled={!canGoPrevious}
                    style={{
                      background: canGoPrevious 
                        ? 'linear-gradient(135deg, #6C3AED 0%, #7C3AED 100%)'
                        : 'rgba(108, 117, 125, 0.3)',
                      border: canGoPrevious 
                        ? '2px solid rgba(108, 58, 237, 0.3)'
                        : '2px solid rgba(108, 117, 125, 0.2)',
                      color: 'white',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      boxShadow: canGoPrevious 
                        ? '0 4px 15px rgba(108, 58, 237, 0.25)'
                        : 'none',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem',
                      cursor: canGoPrevious ? 'pointer' : 'not-allowed'
                    }}
                    onMouseEnter={(e) => {
                      if (canGoPrevious) {
                        e.target.style.transform = 'translateY(-2px) scale(1.1)';
                        e.target.style.boxShadow = '0 6px 20px rgba(108, 58, 237, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (canGoPrevious) {
                        e.target.style.transform = 'translateY(0) scale(1)';
                        e.target.style.boxShadow = '0 4px 15px rgba(108, 58, 237, 0.25)';
                      }
                    }}
                  >
                    <i className="fas fa-chevron-left" />
                  </button>

                  {/* Pagination Indicator */}
                  <div 
                    className="px-4 py-2"
                    style={{
                      background: 'rgba(108, 58, 237, 0.1)',
                      borderRadius: '25px',
                      border: '2px solid rgba(108, 58, 237, 0.2)',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <span 
                      style={{
                        color: '#6C3AED',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {language === 'ar' 
                        ? `${currentPage + 1} / ${totalPages}`
                        : `${currentPage + 1} / ${totalPages}`
                      }
                    </span>
                  </div>

                  {/* Right Arrow Button */}
                  <button
                    className="btn d-flex align-items-center justify-content-center"
                    onClick={goToNextPage}
                    disabled={!canGoNext}
                    style={{
                      background: canGoNext 
                        ? 'linear-gradient(135deg, #6C3AED 0%, #7C3AED 100%)'
                        : 'rgba(108, 117, 125, 0.3)',
                      border: canGoNext 
                        ? '2px solid rgba(108, 58, 237, 0.3)'
                        : '2px solid rgba(108, 117, 125, 0.2)',
                      color: 'white',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      boxShadow: canGoNext 
                        ? '0 4px 15px rgba(108, 58, 237, 0.25)'
                        : 'none',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem',
                      cursor: canGoNext ? 'pointer' : 'not-allowed'
                    }}
                    onMouseEnter={(e) => {
                      if (canGoNext) {
                        e.target.style.transform = 'translateY(-2px) scale(1.1)';
                        e.target.style.boxShadow = '0 6px 20px rgba(108, 58, 237, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (canGoNext) {
                        e.target.style.transform = 'translateY(0) scale(1)';
                        e.target.style.boxShadow = '0 4px 15px rgba(108, 58, 237, 0.25)';
                      }
                    }}
                  >
                    <i className="fas fa-chevron-right" />
                  </button>
                </div>
              </div>
            )}
        </React.Fragment>
      ) : (
        /* Fallback content if no reviews available */
        <div className="row">
          <div className="col-md-4 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="text-warning mb-2">
                  <i className="fas fa-star" />
                  <i className="fas fa-star" />
                  <i className="fas fa-star" />
                  <i className="fas fa-star" />
                  <i className="fas fa-star" />
                </div>
                <p className="card-text">
                  {language === 'ar' 
                    ? '"خدمة ممتازة للتحويل الصوتي بدقة عالية."'
                    : '"Excellent transcription service with high accuracy."'
                  }
                </p>
                <footer className="blockquote-footer">
                  {language === 'ar' ? 'مستخدم راضي' : 'Satisfied User'}
                </footer>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Custom CSS Styles with Mobile Optimizations */}
      <style jsx>{`
        .review-toggle-btn:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 12px 35px rgba(108, 58, 237, 0.5) !important;
        }
        
        .review-toggle-btn:hover .shine-effect {
          transform: translateX(100%) !important;
        }
        
        .review-item .review-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 35px rgba(108, 58, 237, 0.25) !important;
        }
        
        .review-item .review-card:hover .hover-overlay {
          opacity: 1 !important;
        }

        /* Desktop height behavior */
        .reviews-container.collapsed {
          max-height: 400px;
          overflow: hidden;
        }
        
        .reviews-container.expanded {
          max-height: 800px;
          overflow: visible;
        }
        
        .reviews-content {
          height: 100%;
        }

        /* Mobile-specific improvements */
        @media (max-width: 768px) {
          .reviews-container {
            margin: 0 -10px !important;
            border-radius: 10px !important;
          }
          
          /* Remove height constraints on mobile - let content flow naturally */
          .reviews-container.collapsed,
          .reviews-container.expanded {
            max-height: none !important;
            overflow: visible !important;
            height: auto !important;
          }
          
          .reviews-content {
            height: auto !important;
          }
          
          .review-item {
            padding-left: 10px !important;
            padding-right: 10px !important;
          }
          
          .review-card {
            margin-bottom: 1rem !important;
          }
          
          /* Reduce hover effects on mobile for better performance */
          .review-item .review-card:hover {
            transform: none !important;
          }
          
          .review-toggle-btn:hover {
            transform: none !important;
          }
        }

        /* Small mobile devices */
        @media (max-width: 576px) {
          .reviews-section {
            padding: 0 !important;
          }
          
          .reviews-container {
            border-radius: 8px !important;
            padding: 1rem !important;
          }
          
          .review-card {
            border-radius: 10px !important;
          }
        }
        
        /* Bootstrap Grid Reviews Layout */
        .review-card .card {
          box-shadow: 0 12px 30px rgba(108, 58, 237, 0.2), 0 4px 12px rgba(0, 0, 0, 0.1);
          border-radius: 12px;
          transition: all 0.4s ease;
          backdrop-filter: blur(10px);
          margin: 0 auto;
          width: 100%;
          height: 100%;
        }
        
        .review-card:hover .card {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 50px rgba(108, 58, 237, 0.3), 0 10px 25px rgba(0, 0, 0, 0.15);
        }
        
        /* Ensure proper spacing in Bootstrap grid */
        .row.g-4 > * {
          padding: 0.75rem;
        }
        
        .row.g-3 > * {
          padding: 0.5rem;
        }
        
        /* Pagination arrow effects */
        .btn[disabled] {
          opacity: 0.3 !important;
          cursor: not-allowed !important;
        }
        
        /* Mobile optimizations */
        @media (max-width: 768px) {
          .review-card .card {
            border-radius: 10px;
            box-shadow: 0 8px 20px rgba(108, 58, 237, 0.15);
            margin-bottom: 1rem;
          }
          
          .review-card:hover .card {
            transform: translateY(-4px);
            box-shadow: 0 12px 30px rgba(108, 58, 237, 0.25);
          }
        }
      `}</style>

      {/* Full Review Modal */}
      {expandedReview && (
        <div 
          className="modal show d-block"
          style={{ 
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            zIndex: 1050
          }}
          onClick={closeFullReview}
        >
          <div 
            className="modal-dialog modal-dialog-centered modal-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '600px' }}
          >
            <div 
              className="modal-content border-0"
              style={{
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: '0 25px 60px rgba(0, 0, 0, 0.12), 0 8px 24px rgba(108, 58, 237, 0.08)',
                border: '1px solid #E2E8F0'
              }}
            >
              {/* Header */}
              <div 
                className="modal-header border-0"
                style={{
                  background: '#FFFFFF',
                  padding: '2rem 2rem 1.5rem 2rem',
                  position: 'relative',
                  borderBottom: '1px solid #F1F5F9'
                }}
              >
                <div className="d-flex align-items-center w-100">
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: '56px',
                      height: '56px',
                      background: 'linear-gradient(135deg, #EDE9FE, #E0F2FE)',
                      color: '#6C3AED',
                      fontWeight: '700',
                      fontSize: '1.3em',
                      border: '2px solid #E2E8F0',
                      marginRight: language === 'ar' ? '0' : '16px',
                      marginLeft: language === 'ar' ? '16px' : '0'
                    }}
                  >
                    {expandedReview.author.name[0].toUpperCase()}
                  </div>
                  <div className="flex-grow-1">
                    <h4 className="modal-title mb-1" style={{ 
                      fontWeight: '700', 
                      fontSize: '1.15em', 
                      color: '#0F172A',
                      fontFamily: '"Inter", sans-serif'
                    }}>
                      {expandedReview.author.name}
                    </h4>
                    <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                      <div className="d-flex">
                        {renderStars(expandedReview.reviewRating.ratingValue).map((star, i) => (
                          <span key={i} style={{ 
                            color: '#FBBF24', 
                            fontSize: '1em',
                            marginRight: '1px'
                          }}>
                            {star}
                          </span>
                        ))}
                      </div>
                      <span style={{ 
                        background: '#F1F5F9', 
                        padding: '2px 10px', 
                        borderRadius: '9999px', 
                        fontSize: '0.75em',
                        fontWeight: '600',
                        color: '#6C3AED',
                        border: '1px solid #E2E8F0'
                      }}>
                        {expandedReview.reviewRating.ratingValue}/5
                      </span>
                    </div>
                  </div>
                </div>
                
                <button 
                  type="button" 
                  className="position-absolute"
                  style={{ 
                    top: '16px', 
                    right: language === 'ar' ? 'auto' : '16px',
                    left: language === 'ar' ? '16px' : 'auto',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#64748B',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    padding: 0
                  }}
                  onClick={closeFullReview}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#FEE2E2';
                    e.currentTarget.style.color = '#EF4444';
                    e.currentTarget.style.borderColor = '#FECACA';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#F8FAFC';
                    e.currentTarget.style.color = '#64748B';
                    e.currentTarget.style.borderColor = '#E2E8F0';
                  }}
                  aria-label="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              {/* Body */}
              <div 
                className="modal-body"
                style={{ 
                  padding: '2rem',
                  background: '#FFFFFF'
                }}
              >
                {/* Quote Icon */}
                <div className="mb-3">
                  <i 
                    className="fas fa-quote-left"
                    style={{
                      fontSize: '1.5em',
                      color: '#6C3AED',
                      opacity: 0.2
                    }}
                  />
                </div>
                
                {/* Review Text */}
                <div 
                  style={{
                    fontSize: '1.05em',
                    lineHeight: '1.8',
                    color: '#334155',
                    fontWeight: '400',
                    fontFamily: '"Inter", sans-serif',
                    textAlign: language === 'ar' ? 'right' : 'left'
                  }}
                >
                  {expandedReview.reviewBody}
                </div>
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: '1.25rem 2rem',
                  background: '#F8FAFC',
                  borderTop: '1px solid #F1F5F9'
                }}
              >
                <div 
                  className="d-flex justify-content-between align-items-center"
                >
                  <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                    <i className="fas fa-check-circle" style={{ fontSize: '0.9em', color: '#10B981' }} />
                    <span style={{ 
                      color: '#64748B', 
                      fontWeight: '500',
                      fontSize: '0.85em',
                      fontFamily: '"Inter", sans-serif'
                    }}>
                      {language === 'ar' ? 'مستخدم معتمد' : 'Verified User'}
                    </span>
                  </div>
                  
                  <div className="d-flex align-items-center" style={{ gap: '6px', color: '#94A3B8' }}>
                    <i className="fas fa-calendar-alt" style={{ fontSize: '0.8em' }} />
                    <span style={{ fontSize: '0.8em', fontFamily: '"Inter", sans-serif' }}>
                      {new Date(expandedReview.datePublished).toLocaleDateString(
                        language === 'ar' ? 'ar-SA' : 'en-US',
                        { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        }
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;